import { defineComponent, PropType } from 'vue'
import './index.scss'
import gsap from 'gsap'
import PreregisterCountDisplay from './count-display'
import { ModuleBase, GfrToast } from '#components'
import { moduleBaseProps } from '@/components/module/base'
type ModulePreregisterType = 'default' | 'ua'
interface DownloadConfig {
  img: string
  url: string
}
interface PreregisterCountResponse {
  status: 'success' | 'error'
  msg: string
  data?: {
    count?: number
  }
}

const PREREGISTER_COUNT_POLLING_INTERVAL = 60 * 1000
type TiktokAnalytics = {
  track?: (event: string, data?: Record<string, unknown>) => void
}

export default defineComponent({
  name: 'ModulePreregister',
  props: {
    ...moduleBaseProps,
    type: {
      type: String as PropType<ModulePreregisterType>,
      default: 'default'
    },
    logo: String,
    titleImage: String,
    description: String,
    slogan: String,
    trailer: String,
    download: Array as PropType<DownloadConfig[]>,
    preregisterPolicy: String,
    preregisterOpen: Number,
    preregisterCount: Number,
    countDescription: String,
    countSwitch: Number,
    requiredPhone: Number,
    openPhone: Number,
    uaBackgroundOne: String,
    uaBackgroundTwo: String,
    className: null as unknown as PropType<unknown>
  },
  emits: ['complete'],
  setup(props, { slots, emit }) {
    const store = useStore()
    const { config, toast } = storeToRefs(store)
    const main = ref()
    const preregisterCount = ref(props.preregisterCount || 0)
    const description = computed(() => props.description)
    const showCountDisplay = computed(() => props.countSwitch === 1)
    let ctx: any
    let pollingTimer: ReturnType<typeof setTimeout> | null = null
    let isPollingActive = false
    const downloadClick = (index: number) => {
      const ttq = (window as Window & { ttq?: TiktokAnalytics }).ttq
      ttq?.track?.('ClickButton', {
        content_type: 'product',
        content_name: `Preregister_Download_${index + 1}`
      })
    }
    const clearPollingTimer = () => {
      if (pollingTimer) {
        clearTimeout(pollingTimer)
        pollingTimer = null
      }
    }
    const fetchPreregisterCount = async () => {
      try {
        if (!showCountDisplay.value) {
          return
        }
        const langId = config.value.id
        if (!langId) {
          return
        }
        const res = await $fetch<PreregisterCountResponse>(`/api/preregister-count?lang_id=${langId}`)
        const count = res?.data?.count
        if (res?.status === 'success' && typeof count === 'number') {
          preregisterCount.value = count
        }
      } catch (error) {
        console.error('Fetch preregister count failed:', error)
      } finally {
        clearPollingTimer()
        if (isPollingActive && showCountDisplay.value) {
          pollingTimer = setTimeout(fetchPreregisterCount, PREREGISTER_COUNT_POLLING_INTERVAL)
        }
      }
    }
    watch(
      () => props.preregisterCount,
      (value) => {
        if (typeof value === 'number') {
          preregisterCount.value = value
        }
      }
    )
    watch(showCountDisplay, (visible) => {
      if (visible && isPollingActive) {
        fetchPreregisterCount()
      } else if (!visible) {
        clearPollingTimer()
      }
    })
    onMounted(() => {
      isPollingActive = true
      if (showCountDisplay.value) {
        fetchPreregisterCount()
      }
      ctx = gsap.context((self) => {
        const title = self.selector && self.selector('.anime-title')
        const slogan = self.selector && self.selector('.anime-slogan')
        const link = self.selector && self.selector('.anime-link')
        const button = self.selector && self.selector('.anime-button')
        gsap
          .timeline({
            onComplete: () => emit('complete')
          })
          .fromTo(title, { opacity: 0, y: 90 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power4' }, 0.15)
          .fromTo(slogan, { opacity: 0, y: 80 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power4' }, 0.3)
          .fromTo(link, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power4' }, 0.7)
          .fromTo(button, { opacity: 0, y: 120 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power4' }, 0.9)
      }, main.value)
    })
    onUnmounted(() => {
      isPollingActive = false
      clearPollingTimer()
      ctx.revert() // <- Easy Cleanup!
    })
    return () => (
      <ModuleBase
        name={props.name}
        className={props.className}
        hasVideo={props.video !== ''}
        video={props.video}
        desktopBackground={props.desktopBackground}
        mobileBackground={props.mobileBackground}
        videoPoster={props.desktopBackground}
      >
        <section ref={main} class={['module-preregister', props.type]}>
          <div class="module-preregister-brand">
            {props.titleImage && (
              <picture class={['module-preregister-title', 'anime-title']}>
                <img class="module-preregister-title-img" src={props.titleImage} alt={props.name} />
              </picture>
            )}
            {description.value && <p class={'module-preregister-slogan anime-slogan'}>{description.value}</p>}
          </div>
          {props.download && props.download.length > 0 && (
            <div class={['module-preregister-links', `anime-link`, props.type]}>
              <div class={['module-preregister-download', props.type]}>
                {props.download?.map((item, index) => (
                  <a
                    class={['module-preregister-link']}
                    href={item.url}
                    target="_blank"
                    onClick={() => {
                      downloadClick(index)
                    }}
                  >
                    <img class="module-preregister-line-img" src={item.img} alt={props.name} />
                  </a>
                ))}
              </div>
            </div>
          )}
          {showCountDisplay.value && (
            <PreregisterCountDisplay
              className={['anime-button', !props.preregisterOpen && 'disabled']}
              value={preregisterCount.value}
              countDescription={props.countDescription}
            />
          )}
          <GfrToast
            show={toast.value.show}
            duration={toast.value.duration}
            message={toast.value.message}
            closeOnClick={toast.value.closeOnClick}
            onUpdate:show={(val) => (toast.value.show = val)}
          />
          {slots.default?.()}
        </section>
      </ModuleBase>
    )
  }
})
